import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { getSampleTypes } from "../repositories/SampleRepository";

interface SampleType {
  SampleTypeId: number;
  SampleTypeName: string;
}

const ALL_OPTION: SampleType = { SampleTypeId: 0, SampleTypeName: "ALL" };

interface SampleFilterSelectorProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

export const SampleFilterSelector = ({
  value,
  onChange,
  disabled = false,
}: SampleFilterSelectorProps) => {
  const [samples, setSamples] = useState<SampleType[]>([ALL_OPTION]);
  const [selectedSample, setSelectedSample] = useState<SampleType>(ALL_OPTION);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const loadData = async () => {
    try {
      setIsLoading(true);
      const resp = await getSampleTypes();
      if (resp.ok) {
        const rows = get(resp, "data.tables[0].rows", []) as SampleType[];
        setSamples([ALL_OPTION, ...rows]);
      } else {
        setError(resp.message ?? "Error loading sample types");
      }
    } catch {
      setError("Error loading sample types");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (value === 0) {
      setSelectedSample(ALL_OPTION);
    } else {
      const sample = samples.find((s) => s.SampleTypeId === value);
      setSelectedSample(sample ?? ALL_OPTION);
    }
  }, [value, samples]);

  return (
    <>
      <Autocomplete
        options={samples}
        getOptionLabel={(option) => option.SampleTypeName}
        value={selectedSample}
        onChange={(_event, newValue) => {
          const selected = newValue ?? ALL_OPTION;
          setSelectedSample(selected);
          onChange(selected.SampleTypeId);
        }}
        isOptionEqualToValue={(option, val) =>
          option.SampleTypeId === val.SampleTypeId
        }
        disableClearable
        disabled={disabled || isLoading}
        loading={isLoading}
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            label="Sample Type"
            variant="outlined"
            fullWidth
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
